import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ClockIcon from '../assets/icon-clock.svg';
import Loader from './Loader';
import '../Slider.css';
import CountdownTimer from './CountdownTimer';
import TimePicker from './TimePicker';
import CountdownContext from '../context/CountdownContext';
import LogoCarousel from './Carousel';
import HomeShowcaseWheel from './HomeShowcaseWheel';
import { useAppConfig } from '../context/AppConfigContext';
import { APP_DEFAULTS } from '../config';
import { isCampaignEnded } from '../utils/campaign';

const StartButton: React.FC = () => {
  const navigate = useNavigate();
  const { setTargetDate } = useContext(CountdownContext);
  const { config } = useAppConfig();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const loaderDelay = config.loaderDelay ?? APP_DEFAULTS.loaderDelay;
  const taglines = config.taglines?.length ? config.taglines : APP_DEFAULTS.taglines;
  const allowLocalTimerOverride =
    config.allowLocalTimerOverride ?? APP_DEFAULTS.allowLocalTimerOverride;
  const campaignIsEnded = isCampaignEnded(config.campaignEndsAt);
  const campaignIsUnavailable = !config.campaignEnabled || campaignIsEnded;

  const campaignMessage = useMemo(() => {
    if (campaignIsEnded) {
      return 'Campaign has ended.';
    }

    if (!config.campaignEnabled) {
      return 'Campaign is currently disabled.';
    }

    return '';
  }, [campaignIsEnded, config.campaignEnabled]);

  const disabledLabel = campaignIsEnded ? 'CAMPAIGN ENDED' : 'CAMPAIGN DISABLED';

  useEffect(() => {
    console.log('[StartButton] Campaign state', {
      countdownTargetDate: config.countdownTargetDate,
      campaignEnabled: config.campaignEnabled,
      campaignEndsAt: config.campaignEndsAt,
      allowLocalTimerOverride,
      campaignIsEnded,
      campaignIsUnavailable,
      campaignMessage,
      now: Date.now(),
    });
  }, [
    allowLocalTimerOverride,
    campaignIsEnded,
    campaignIsUnavailable,
    campaignMessage,
    config.campaignEnabled,
    config.campaignEndsAt,
    config.countdownTargetDate,
  ]);

  const handleStartNowClick = () => {
    if (campaignIsUnavailable) {
      return;
    }

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate('/contact');
    }, loaderDelay);
  };

  useEffect(() => {
    const sleed1 = document.getElementById('sleed1') as HTMLElement | null;
    if (!sleed1) {
      return;
    }

    let position = 0;
    const interval = setInterval(() => {
      position += 1.25;
      sleed1.style.backgroundPosition = `${position}px`;
      sleed1.style.transitionDuration = '2.5ms';
    }, 40);

    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ width: '100%', minHeight: '100vh' }}>
      {allowLocalTimerOverride && (
        <img
          src={ClockIcon}
          alt="Clock Icon"
          className="clock-icon"
          onClick={() => setIsModalOpen(true)}
        />
      )}
      <div style={{ position: 'relative', width: '100%', minHeight: '100vh', overflowY: 'auto' }}>
        <Loader show={loading} />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          width: '100%',
          padding: '40px 16px',
          boxSizing: 'border-box',
        }}>
          <HomeShowcaseWheel />
          <div className="typing-slider" style={{ marginTop: '40px', marginBottom: '32px' }}>
            {taglines.map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
          {campaignMessage && (
            <p style={{ marginBottom: '16px', fontSize: '14px', color: '#fca5a5', textAlign: 'center' }}>
              {campaignMessage}
            </p>
          )}
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center', width: '100%' }}>
            <CountdownTimer
              onStart={handleStartNowClick}
              isDisabled={campaignIsUnavailable}
              disabledLabel={disabledLabel}
              forceComplete={campaignIsUnavailable}
            />
          </div>
          <div style={{ marginTop: '32px', width: '100%' }}>
            <LogoCarousel />
          </div>
        </div>
      </div>

      {allowLocalTimerOverride && isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-black p-4 rounded">
            <button onClick={() => setIsModalOpen(false)} className="close-button">
              &times;
            </button>
            <TimePicker setTargetDate={setTargetDate} />
          </div>
        </div>
      )}
    </div>
  );
};

export default StartButton;
